import { Reveal } from "@/components/Reveal";
import { SectionParticles } from "@/components/SectionParticles";
import { SectionBg } from "@/components/SectionBg";
import { AccordionList } from "@/components/Accordion";
import { PREVIEW_MODE } from "@/lib/campaign";
import { REPLAY } from "@/lib/replay";

/* REPLAY SECTION 10 — FAQ (id="faq") */
const FAQS = [
  {
    title: "Is the Benefit-to-Keys strategy session really complimentary?",
    body: "Yes. There is no fee for the initial educational session and no obligation to apply. Ordinary loan and third-party costs may apply only if a person later proceeds and will be disclosed through the applicable process.",
  },
  {
    title: "Is this an official VA event or an eligibility determination?",
    body: "No. This replay and strategy session are educational, are not sponsored or endorsed by the Department of Veterans Affairs, and do not determine VA eligibility or entitlement. Verification requires the appropriate VA and lender process.",
  },
  {
    title: "Will watching or requesting the initial session affect my credit?",
    body: "No hard credit pull is required merely to watch the replay or attend the initial educational conversation.",
  },
  {
    title: "What if I used VA financing before?",
    body: "Prior use does not by itself answer whether entitlement may be restored, reused, or remain available. The relevant history and documentation must be verified; no entitlement result is promised on this page.",
  },
  {
    title: "Does VA financing always mean no down payment or no cash needed?",
    body: "No. Some eligible borrowers who qualify may have a no-down-payment path, but other funds and costs may still apply. Down payment, deposits, inspections, appraisal, closing costs, prepaid items, reserves, and any applicable funding fee must be reviewed individually.",
  },
  {
    title: "Will a seller accept a VA-financed offer?",
    body: "Seller decisions depend on the full offer, property, market, and transaction. The session can organize offer-coordination questions, but seller acceptance is never guaranteed.",
  },
  {
    title: "How long is the replay available?",
    body: "Replay access is available through this page.",
  },
  {
    title: "What happens after I request a session?",
    body: "The calendar confirms an available time. Any reminders are sent only according to captured consent and the configured workflow. The session organizes questions and next actions; eligibility, terms, approval, and personalized mortgage answers may require a separate application and documentation.",
  },
];

export const ReplayFAQ = () => (
  <section id="faq" className="relative overflow-hidden px-5 md:px-10 lg:px-16 py-28 md:py-36 hairline-t" data-testid="replay-faq-section">
    <SectionBg img="bg-home-4.jpg" strength={0.93} />
    <SectionParticles variant="keys" />
    <div className="relative z-10 max-w-4xl mx-auto text-left">
      <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] mb-12">[008] — COMMON QUESTIONS</p>
      <Reveal>
        <AccordionList items={FAQS} testPrefix="replay-faq-accordion" />
      </Reveal>
    </div>
  </section>
);

/* REPLAY SECTION 11 — FINAL BOOKING (id="book") */
const TRUST_ROWS = [
  "No fee for the initial educational strategy session",
  "Private conversation",
  "No obligation to apply",
  "No hard credit pull required merely for the initial educational conversation",
];

export const ReplayBook = () => (
  <section
    id="book"
    className="relative px-5 md:px-10 lg:px-16 py-28 md:py-40 overflow-hidden"
    style={{ background: "var(--background-deep)" }}
    data-testid="replay-book-section"
  >
    <SectionBg img="bg-home-5.jpg" strength={0.93} />
    <div
      aria-hidden="true"
      className="absolute inset-0 opacity-[0.05]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
        backgroundSize: "72px 72px",
      }}
    />
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{ background: "radial-gradient(ellipse 60% 45% at 30% 40%, rgba(148,222,82,0.09), transparent)" }}
    />
    <SectionParticles variant="houses" />
    <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center text-left">
      <div>
        <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] mb-10">[009] — REQUEST A PRIVATE REVIEW</p>
        <Reveal>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight text-white mb-8">
            VERIFY THE QUESTIONS. MODEL THE INPUTS. MAP THE NEXT MOVE.
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-[rgba(255,255,255,0.78)] mb-10">
            Request a private Benefit-to-Keys Strategy Session if you want to organize a possible Florida home
            purchase, replace VA financing myths with questions that can be verified, and identify the next
            responsible action.
          </p>
        </Reveal>
        <ul className="space-y-4">
          {TRUST_ROWS.map((b, i) => (
            <Reveal key={b} delay={i * 0.08}>
              <li className="flex items-start gap-3 text-sm md:text-base text-[rgba(255,255,255,0.82)]" data-testid={`book-trust-row-${i}`}>
                <span aria-hidden="true" className="text-[var(--accent)]">✓</span>
                {b}
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
      <Reveal>
        <div className="glass-panel rounded-[6px] overflow-hidden" data-testid="booking-calendar-panel">
          <div className="px-6 py-4 border-b border-[var(--line)]">
            <p className="font-mono text-[10px] tracking-[0.24em] text-[var(--accent)]">
              COMPLIMENTARY BENEFIT-TO-KEYS STRATEGY SESSION
            </p>
          </div>
          {REPLAY.calendarEmbedUrl ? (
            <iframe
              src={REPLAY.calendarEmbedUrl}
              title="Schedule your Complimentary Benefit-to-Keys Strategy Session"
              className="w-full"
              style={{ minHeight: "620px", border: "0" }}
              loading="lazy"
            />
          ) : (
            PREVIEW_MODE && (
              <div className="p-10 text-center" data-testid="calendar-pending-placeholder">
                <span aria-hidden="true" className="inline-block w-14 h-14 rounded-[6px] border border-[var(--accent)] text-[var(--accent)] leading-[3.5rem] text-xl mb-6 opacity-60">▦</span>
                <p className="font-mono text-[11px] tracking-[0.18em] text-[var(--muted)] leading-relaxed">
                  STRATEGY-SESSION CALENDAR PENDING CONFIGURATION
                </p>
                <p className="text-xs text-[var(--muted)] mt-4 leading-relaxed max-w-sm mx-auto">
                  The scheduling calendar appears here once connected. Sessions are private, educational, and
                  carry no obligation to apply.
                </p>
              </div>
            )
          )}
        </div>
      </Reveal>
    </div>
  </section>
);
